import { InteractionType } from "@azure/msal-browser";
import { MsalAuthenticationTemplate, MsalProvider } from "@azure/msal-react";
import { QueryClientProvider } from "@tanstack/react-query";
import { FC, useEffect } from "react";
import { BrowserRouter, Link, Route, Routes } from "react-router-dom";
import { msalApp } from "../context/authContext";
import { BackdropProvider } from "../context/modalContext";
import NotificationDispatch, { useNotification } from "../context/notificationContext";
import { PageProvider, usePageContext } from "../context/pageContext";
import PermissionProvider, { usePermissionsContext } from "../context/permissionContext";
import { I18nProvider, useI18n } from "../hooks/useTranslations";
import { queryClient } from "../http/query";
import { ProcessingRequestStatuses } from "../http/processingRequest/types";
import { ProcessingTypeViews } from "../http/processingType/type";
import AccountButton from "./AccountButton";
import styles from "./App.module.css";
import CostCenters from "./costCenters/CostCenters";
import ChevronRight from "./icons/ChevronRight";
import Items from "./items/Items";
import Labels from "./labels/Labels";
import LanguageSelector from "./LanguageSelector";
import MenuButton from "./MenuButton";
import Packagings from "./packagings/Packagings";
import Priorities from "./priorities/Priorities";
import CreateProcessingRequest from "./processingRequests/CreateProcessingRequestForm/CreateProcessingRequest";
import ItemGroupedRequests from "./processingRequests/ItemGroupedRequests";
import ProcessingRequestDetail from "./processingRequests/ProcessingRequestDetail";
import ProcessingRequests from "./processingRequests/ProcessingRequests";
import ProcessingTypes from "./processingTypes/ProcessingTypes";
import Suppliers from "./suppliers/Suppliers";
import ErrorText from "./ui/ErrorText";
import Header from "./ui/Header";
import Icon from "./ui/Icon";
import LoadingSpinner from "./ui/LoadingSpinner";
import Notification from "./ui/Notification";
import Users from "./users/Users";

const Spinner = () => (
  <div className={styles.spinner}>
    <LoadingSpinner />
  </div>
);

const App: FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <MsalProvider instance={msalApp}>
        <MsalAuthenticationTemplate
          interactionType={InteractionType.Redirect}
          loadingComponent={Spinner}
          authenticationRequest={{ redirectUri: window.location.origin }}
        >
          <BrowserRouter>
            <PermissionProvider>
              <I18nProvider>
                <PageProvider>
                  <AppRoutes />
                </PageProvider>
              </I18nProvider>
            </PermissionProvider>
          </BrowserRouter>
        </MsalAuthenticationTemplate>
      </MsalProvider>
    </QueryClientProvider>
  );
};

export default App;

const AppRoutes: FC = () => {
  const i18n = useI18n();
  const pageContext = usePageContext();
  const [{ show, severity, message, sticky }, dispatch] = useNotification();
  const permissions = usePermissionsContext();

  useEffect(() => {
    permissions.preferredLanguage && i18n.setLanguage(permissions.preferredLanguage);
  }, [permissions.preferredLanguage]);

  // if the permissions instance has not been instanced yet or they are still loading
  if (!permissions || permissions.isLoading) {
    return (
      <div className={styles.center}>
        <LoadingSpinner />
      </div>
    );
  }

  // in case of error
  if (permissions.error) {
    return (
      <div className={styles.center}>
        <ErrorText text={i18n.t.errors.genericError} />
      </div>
    );
  }

  // if the permissions has been loaded but the user has no access
  if (!permissions.hasAccess) {
    return (
      <div className={styles.center}>
        <ErrorText text={i18n.t.common.youAreNotAllowedToAccessThisApplication} />
      </div>
    );
  }

  return (
    <>
      <BackdropProvider>
        <NotificationDispatch.Provider value={dispatch}>
          <div className={styles.content}>
            <nav className={styles.nav}>
              <MenuButton />
              <div className={styles.title}>
                <div className={styles.link}>
                  {pageContext.page?.url ? (
                    <Link to={pageContext.page.url}>{pageContext.page.title}</Link>
                  ) : (
                    <div>{pageContext.page?.title ?? i18n.t.common.home}</div>
                  )}
                </div>
                {(pageContext.requestId || pageContext.subPath) && (
                  <>
                    <Icon glyph={ChevronRight} />
                    {pageContext.requestId && <div> #{pageContext.requestId}</div>}
                    {pageContext.subPath && (
                      <div className={styles.title}>
                        <div>
                          <div>{pageContext.subPath}</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
                {import.meta.env.MODE === "staging" && (
                  <div className={styles.test}>{i18n.t.common.testEnvironment}</div>
                )}
              </div>
              <div className={styles.buttons}>
                <LanguageSelector />
                <AccountButton />
              </div>
            </nav>

            <div className={styles["page-content"]}>
              <Routes>
                <Route path="/" element={<ProcessingRequests views={[ProcessingTypeViews.AllRequests]} />} />
                <Route path="/requests" element={<ProcessingRequests views={[ProcessingTypeViews.AllRequests]} />} />
                <Route
                  path="/requests/helmets"
                  element={<ProcessingRequests views={[ProcessingTypeViews.Helmets]} />}
                />
                <Route
                  path="/requests/closed_and_discarded"
                  element={
                    <ProcessingRequests
                      customFilters={[
                        {
                          fieldname: "processingRequest.status",
                          values: [ProcessingRequestStatuses.Closed, ProcessingRequestStatuses.Discarded],
                        },
                      ]}
                      views={Object.values(ProcessingTypeViews)}
                    />
                  }
                />
                <Route
                  path="/requests/on_going"
                  element={
                    <ProcessingRequests
                      customFilters={[
                        {
                          fieldname: "processingRequest.status",
                          values: [
                            ProcessingRequestStatuses.Open,
                            ProcessingRequestStatuses.Accepted,
                            ProcessingRequestStatuses.WaitingConfirmation,
                            ProcessingRequestStatuses.OfferApproved,
                            ProcessingRequestStatuses.InProgress,
                            ProcessingRequestStatuses.Refused,
                          ],
                        },
                      ]}
                      views={Object.values(ProcessingTypeViews)}
                    />
                  }
                />
                <Route path="/requests/new" element={<CreateProcessingRequest />} />
                <Route path="/requests/:id" element={<ProcessingRequestDetail />} />
                {!!permissions.isWarehouseOperator && (
                  <>
                    <Route path="/suppliers" element={<Suppliers />} />
                    <Route path="/labels" element={<Labels />} />
                    <Route path="/packagings" element={<Packagings />} />
                    <Route path="/types" element={<ProcessingTypes />} />
                    <Route path="/priorities" element={<Priorities />} />
                    <Route path="/cost_centers" element={<CostCenters />} />
                    <Route path="/requests/grouped_by_items" element={<ItemGroupedRequests />} />
                  </>
                )}
                {!!permissions.hasUserMananagement && <Route path="/users" element={<Users />} />}
                {(permissions.isWarehouseOperator || permissions.isPurchaseOffice) && (
                  <Route path="/items" element={<Items />} />
                )}
                <Route
                  path="*"
                  element={
                    <>
                      <Header title={i18n.t.common.notFound} />
                      <ErrorText text={i18n.t.common.resourceNotFound} />
                    </>
                  }
                />
              </Routes>
            </div>
          </div>
          {show && <Notification type={severity} text={message} sticky={sticky} />}
        </NotificationDispatch.Provider>
      </BackdropProvider>
    </>
  );
};
